import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, UserCheck, Users } from "lucide-react";
import { normalize } from "@/components/Brutal";

/**
 * Combobox com busca para selecionar Cooperado/Comunitário.
 * Mostra: Nome · Tipo · CPF · Comunidade · Função.
 * Props:
 *  - items: lista de cooperados
 *  - value: id selecionado
 *  - onChange: fn(id) chamada ao selecionar
 *  - tipoFilter: "all" | "cooperado" | "comunitario"
 *  - testid
 */
export default function CooperadoCombobox({ items = [], value, onChange, tipoFilter = "all", testid }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const filtered = useMemo(() => {
    const nq = normalize(query);
    return items.filter((c) => {
      if (tipoFilter !== "all" && (c.tipo || "cooperado") !== tipoFilter) return false;
      if (!nq) return true;
      return (
        normalize(c.nome).includes(nq) ||
        normalize(c.cpf).includes(nq) ||
        normalize(c.community_nome).includes(nq) ||
        normalize(c.funcao).includes(nq) ||
        normalize(c.telefone).includes(nq)
      );
    }).slice(0, 50);
  }, [items, query, tipoFilter]);

  const selected = items.find((c) => c.id === value);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      {selected ? (
        <div className="flex items-center justify-between gap-2 border-2 border-[#111] bg-[#F4F8F5] p-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest font-mono-data ${selected.tipo === "comunitario" ? "bg-[#FF4F00] text-white" : "bg-[#3D6B57] text-white"}`}>
                {selected.tipo === "comunitario" ? "COM" : "COO"}
              </span>
              <span className="font-medium truncate">{selected.nome}</span>
              {selected.cpf && <span className="font-mono-data text-[11px] text-[#555]">· CPF {selected.cpf}</span>}
            </div>
            <div className="font-mono-data text-[10px] text-[#777] mt-0.5 truncate">
              {selected.community_nome && <>Comun.: <b>{selected.community_nome}</b></>}
              {selected.funcao && <> · {selected.funcao}</>}
              {selected.telefone && <> · 📱 {selected.telefone}</>}
            </div>
          </div>
          <button type="button" onClick={clear} className="shrink-0 p-1.5 border-2 border-[#111] hover:bg-[#E60000] hover:text-white" data-testid={testid ? `${testid}-clear` : undefined} title="Trocar seleção">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 border-2 border-[#111] bg-white p-2 focus-within:border-[#FF4F00]">
          <Search className="w-4 h-4 text-[#555] shrink-0" />
          <input
            type="text"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            placeholder="Buscar por nome, CPF, comunidade ou função..."
            className="flex-1 outline-none text-sm bg-transparent font-mono-data"
            data-testid={testid ? `${testid}-input` : undefined}
          />
        </div>
      )}

      {open && !selected && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-80 overflow-y-auto border-2 border-[#111] bg-white shadow-xl" data-testid={testid ? `${testid}-list` : undefined}>
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs font-mono-data text-[#777] uppercase tracking-widest">
              Nenhum resultado para "{query}"
            </div>
          ) : (
            filtered.map((c) => {
              const isCom = c.tipo === "comunitario";
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange(c.id); setOpen(false); setQuery(""); }}
                  data-testid={testid ? `${testid}-opt-${c.id}` : undefined}
                  className="w-full text-left px-3 py-2 hover:bg-[#F4F8F5] border-b border-[#eee] last:border-b-0 flex items-start gap-2"
                >
                  <span className={`shrink-0 w-7 h-7 flex items-center justify-center ${isCom ? "bg-[#FF4F00] text-white" : "bg-[#3D6B57] text-white"}`}>
                    {isCom ? <Users className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-medium">{c.nome}</span>
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest font-mono-data ${isCom ? "bg-[#FF4F00]/15 text-[#FF4F00]" : "bg-[#3D6B57]/15 text-[#3D6B57]"}`}>
                        {isCom ? "Comunitário" : "Cooperado"}
                      </span>
                      {c.cpf && <span className="font-mono-data text-[11px] text-[#555]">CPF {c.cpf}</span>}
                    </div>
                    <div className="font-mono-data text-[10px] text-[#777] mt-0.5">
                      {c.community_nome || "— sem comunidade —"}
                      {c.funcao && <> · {c.funcao}</>}
                      {c.telefone && <> · 📱 {c.telefone}</>}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
